import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { modulesConfig, GROUP_META } from "../../config/modules.config";
import { usePermission } from "../../rbac/usePermission";
import { useUIStore } from "../../stores/uiStore";
import { cn } from "../../lib/utils";

export function Sidebar() {
  const location = useLocation();
  const { hasPermission, hasRole } = usePermission();
  const { sidebarCollapsed, toggleSidebar, closeMobileMenu } = useUIStore();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    "user-management": true,
    "security": true,
    "support": true,
  });

  const toggleGroup = (groupKey: string) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Group modules
  const { role } = usePermission();
  const groupedModules = modulesConfig.reduce((acc, mod) => {
    if (mod.role && !hasRole(mod.role)) return acc;
    if (mod.roles && (!role || !mod.roles.includes(role))) return acc;
    if (mod.permission && !hasPermission(mod.permission)) return acc;
    const group = mod.group || "ungrouped";
    if (!acc[group]) acc[group] = [];
    acc[group].push(mod);
    return acc;
  }, {} as Record<string, typeof modulesConfig>);

  const renderIcon = (name: string, className = "w-4 h-4 shrink-0") => {
    // @ts-expect-error dynamic lookup
    const IconComp = Icons[name] || Icons.Circle;
    return <IconComp className={className} />;
  };

  return (
    <aside
      className={cn(
        "h-screen bg-card border-r border-border flex flex-col transition-all duration-300 select-none z-30",
        sidebarCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Brand header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border shrink-0">
        {!sidebarCollapsed && (
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm shadow-md shrink-0">
              CT
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-sm leading-tight truncate text-foreground">
                Collect & Track
              </span>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                Back-office
              </span>
            </div>
          </div>
        )}

        {sidebarCollapsed && (
          <div className="w-8 h-8 rounded-lg bg-primary mx-auto flex items-center justify-center text-primary-foreground font-bold text-sm shadow-md">
            CT
          </div>
        )}

        <button
          type="button"
          onClick={toggleSidebar}
          className="hidden md:flex p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          title={sidebarCollapsed ? "Agrandir le menu" : "Réduire le menu"}
        >
          {sidebarCollapsed ? (
            <Icons.ChevronRight className="w-4 h-4" />
          ) : (
            <Icons.ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation list */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6 scrollbar-hide">
        {/* Ungrouped items (Dashboard) */}
        {groupedModules["ungrouped"] && (
          <div className="space-y-1">
            {groupedModules["ungrouped"].map((mod) => (
              <NavLink
                key={mod.key}
                to={mod.path}
                onClick={closeMobileMenu}
                end={mod.path === "/"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    sidebarCollapsed && "justify-center px-0"
                  )
                }
                title={sidebarCollapsed ? mod.label : undefined}
              >
                {renderIcon(mod.icon)}
                {!sidebarCollapsed && <span>{mod.label}</span>}
              </NavLink>
            ))}
          </div>
        )}

        {/* Grouped sections */}
        {Object.entries(groupedModules).map(([groupKey, mods]) => {
          if (groupKey === "ungrouped") return null;
          const meta = GROUP_META[groupKey] || { label: groupKey, icon: "Folder" };
          const isOpen = openGroups[groupKey] ?? true;

          return (
            <div key={groupKey} className="space-y-1">
              {!sidebarCollapsed ? (
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKey)}
                  className="w-full flex items-center justify-between px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-foreground transition-colors"
                >
                  <span className="flex items-center gap-1.5">
                    {renderIcon(meta.icon, "w-3.5 h-3.5")}
                    {meta.label}
                  </span>
                  <Icons.ChevronDown
                    className={cn(
                      "w-3.5 h-3.5 transition-transform duration-200",
                      !isOpen && "-rotate-90"
                    )}
                  />
                </button>
              ) : (
                <div className="w-full h-px bg-border my-2" />
              )}

              {(isOpen || sidebarCollapsed) && (
                <div className="space-y-1 mt-1">
                  {mods.map((mod) => (
                    <NavLink
                      key={mod.key}
                      to={mod.path}
                      onClick={closeMobileMenu}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                          sidebarCollapsed && "justify-center px-0"
                        )
                      }
                      title={sidebarCollapsed ? mod.label : undefined}
                    >
                      {renderIcon(mod.icon)}
                      {!sidebarCollapsed && <span>{mod.label}</span>}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      {!sidebarCollapsed && (
        <div className="p-4 border-t border-border shrink-0 bg-muted/20">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>RBAC Stack v1.0</span>
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 font-medium">
              Actif
            </span>
          </div>
        </div>
      )}
    </aside>
  );
}
